/**
 * useExecutionDetail
 *
 * Loads a single ``Execution`` by ID and keeps it live-updated while the
 * status is non-terminal.  Used by ``ExecutionDetailPage`` so that opening
 * an in-flight run streams step progress without the operator needing to
 * refresh; for a completed run, the poller never starts and the hook makes
 * a single fetch.
 *
 * The hook is intentionally separate from ``run-store`` — that store is
 * scoped to the manual-launch (Split-Pane) flow and tracks automation /
 * parameter state we don't need on the detail page.  Keeping this hook
 * standalone means the page can also be embedded inside other surfaces
 * (queue inspector, live-runs row preview) without dragging in launch
 * state.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getExecution,
  createExecutionPoller,
  isExecutionTerminal,
} from '../api'
import type { Execution } from '../types'

export interface UseExecutionDetailResult {
  execution: Execution | null
  isLoading: boolean
  error: string | null
  /**
   * Force a fresh fetch.  Useful after an abort/approve/reject mutation —
   * the poller will resume if the new status is still non-terminal.
   */
  refetch: () => Promise<void>
}

/**
 * Fetch + poll a single execution by ID.
 *
 * Polling:
 *   - Starts only after the initial fetch returns a non-terminal status.
 *   - Reuses ``createExecutionPoller`` (1s interval — matches the manual
 *     run page so live state stays in sync if the operator has both pages
 *     open).
 *   - Stops automatically when the poller reports a terminal status, or
 *     when the hook unmounts / ``executionId`` changes.
 */
export function useExecutionDetail(
  executionId: string | undefined
): UseExecutionDetailResult {
  const [execution, setExecution] = useState<Execution | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Hold the poller cleanup so refetch + unmount can both stop it.
  const stopPollingRef = useRef<(() => void) | null>(null)

  const stopPolling = useCallback(() => {
    if (stopPollingRef.current) {
      stopPollingRef.current()
      stopPollingRef.current = null
    }
  }, [])

  const startPolling = useCallback((id: string) => {
    // Defensive: never run two pollers for the same execution.
    if (stopPollingRef.current) {
      stopPollingRef.current()
    }
    stopPollingRef.current = createExecutionPoller(
      id,
      (updated) => {
        setExecution(updated)
      },
      (pollError) => {
        // Surface poll errors but don't drop the last good snapshot.
        setError(pollError.message)
      },
      () => {
        // Terminal — poller has already cleared its own interval.
        stopPollingRef.current = null
      }
    )
  }, [])

  const fetchOnce = useCallback(
    async (id: string) => {
      setIsLoading(true)
      setError(null)
      try {
        const fresh = await getExecution(id)
        setExecution(fresh)
        if (!isExecutionTerminal(fresh)) {
          startPolling(id)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load execution')
      } finally {
        setIsLoading(false)
      }
    },
    [startPolling]
  )

  // Drive fetches off ``executionId``.  Polling is torn down on unmount and
  // whenever the operator navigates to a different execution.
  useEffect(() => {
    if (!executionId) {
      setExecution(null)
      setError(null)
      setIsLoading(false)
      stopPolling()
      return
    }

    fetchOnce(executionId)
    return () => {
      stopPolling()
    }
    // ``fetchOnce`` and ``stopPolling`` are stable (wrapped in ``useCallback``)
    // but include them explicitly so eslint-react-hooks is happy.
  }, [executionId, fetchOnce, stopPolling])

  const refetch = useCallback(async () => {
    if (!executionId) return
    stopPolling()
    await fetchOnce(executionId)
  }, [executionId, fetchOnce, stopPolling])

  return { execution, isLoading, error, refetch }
}
